import {useMemo, useState} from 'react';
import {ValidationResult, ValidationRules} from './types';
import {validate} from './validate';

/**
 * Hook for form validation
 *
 * @example
 * import React, {useState} from 'react';
 * import {RsnButton, RsnInput} from '@raison/ui-kit';
 *
 * function ValidationExample () {
 *     const [form, setForm] = useState({
 *         name: '',
 *         email: '',
 *         about: '', //optional
 *     })
 *
 *     const {signalChangeTry, signalSubmitTry, getHelperProps} = useValidateForm(inputValues, {
 *         name: [required()],
 *         email: [required(), email()]
 *     });
 *
 *     const handleChange = (e) => {
 *         const key = e.target.name as keyof typeof form;
 *         signalChangeTry(key)
 *         setForm( prev => ({ ...prev, [key]: e.target.value })
 *     }
 *
 *     const handleSubmit = withoutDefaults(()=>{
 *         if(signalSubmitTry()){
 *             //do something
 *         }
 *     })
 *
 *     return (
 *         <form onSubmit={handleSubmit}>
 *             <RsnInput name='name' label='Name' onChange={handleChange} {...getHelperProps('name')} />
 *             <RsnInput name='email' label='Email' onChange={handleChange} {...getHelperProps('email')} />
 *             <RsnInput name='about' label='About' onChange={handleChange} {...getHelperProps('about')} />
 *             <RsnButton.Primary type="submit">Submit!</RsnButton>
 *         </form>
 *     )
 * }
 */
export const useValidateForm = <T extends object>(formData: T, rules: ValidationRules<T>) => {
    const errors: ValidationResult<T> = useMemo(() => validate(formData, rules), [formData, rules]);

    const [submitTried, setSubmitTried] = useState(false);
    const [inputTried, setInputTried] = useState<Partial<Record<keyof T, boolean>>>({});
    const [blurredAfterInput, setBlurred] = useState<Partial<Record<keyof T, boolean>>>({});

    const signalBlur = (key: keyof T) => () => {
        if (!blurredAfterInput[key] && inputTried[key]) setBlurred(prevState => ({...prevState, [key]: true}));
    };
    const signalChangeTry = (key: keyof T) => {
        if (!inputTried[key]) setInputTried(prevState => ({...prevState, [key]: true}));
    };
    const signalSubmitTry = () => {
        if (!submitTried) setSubmitTried(true);
        return Object.keys(errors).length === 0;
    };

    const getHelperProps = (key: keyof T) => {
        if (errors[key] && (submitTried || blurredAfterInput[key])) {
            return {helperText: errors[key], isInvalid: !!errors[key]};
        }
        return {onBlur: signalBlur(key)};
    };

    return {signalSubmitTry, signalChangeTry, getHelperProps};
};
